import { initVersionedSchema, toTextContent } from '@principle-theorem/editor';
import {
  FailedDestinationEntityRecord,
  IDestinationEntity,
  IDestinationEntityJobRunOptions,
  ITranslationMap,
  SkippedDestinationEntityRecord,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Brand,
  Event,
  Interaction,
  SchedulingEvent,
  SchedulingEventReason,
  TreatmentPlan,
  TreatmentStep,
  stafferToNamedDoc,
  toINamedDocuments,
} from '@principle-theorem/principle-core';
import {
  AppointmentStatus,
  CancellationReason,
  EventType,
  IAssociatedTreatment,
  ISchedulingEvent,
  ITag,
  ITreatmentCategory,
  InteractionType,
  ParticipantType,
  SchedulingEventType,
  TreatmentPlanStatus,
  TreatmentStepStatus,
  isTag,
  type IEvent,
  type IInteractionV2,
  type IPatient,
  type IPractice,
  type IStaffer,
  type ITreatmentStep,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  HISTORY_DATE_FORMAT,
  WithPrimaryKey,
  getError,
  initFirestoreModel,
  isINamedDocument,
  resolveSequentially,
  snapshotCombineLatest,
  toMomentTz,
  toNamedDocument,
  toTimestamp,
  where,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, isString, uniqBy } from 'lodash';
import * as moment from 'moment-timezone';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DestinationEntity } from '../../../destination/destination-entity';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientAppointmentDestinationEntity,
  IPatientAppointmentBuildData,
  IPatientAppointmentJobData,
  IPatientAppointmentMigrationData,
  PATIENT_APPOINTMENT_RESOURCE_TYPE,
} from '../../../destination/entities/patient-appointments';
import {
  IStepWithIdentifiers,
  getDefaultTreatmentPlanName,
} from '../../../destination/entities/patient-treatment-plans';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import { PatientIdFilter } from '../../../destination/filters/patient-id-filter';
import { ItemCodeResourceMapType } from '../../../mappings/item-codes-to-xlsx';
import { PracticeMigration } from '../../../practice-migrations';
import { TranslationMapHandler } from '../../../translation-map';
import {
  ExactPatient,
  PatientSourceEntity,
} from '../../source/entities/patient';
import {
  AppointmentSourceEntity,
  ExactAppointment,
  ExactAppointmentStatus,
  type IExactAppointment,
} from '../../source/entities/patient-appointment';
import { PatientNotesSourceEntity } from '../../source/entities/patient-notes';
import { PatientTreatmentSourceEntity } from '../../source/entities/patient-treatments';
import { AppointmentCategoryToTreatmentCategoryMappingHandler } from '../mappings/appointment-category-to-treatment-category';
import { AppointmentRoomToPractionerMappingHandler } from '../mappings/appointment-room-to-practitioner';
import { ExactItemCodeMappingHandler } from '../mappings/item-codes';
import {
  StaffToPracticeMapping,
  resolveExactStaffLocation,
} from '../mappings/practitioner-to-practice-mapping';
import {
  ExactStafferMappingHandler,
  resolveExactStaffer,
} from '../mappings/staff';
import {
  EXACT_APPOINTMENT_STEP_LINK_RESOURCE_TYPE,
  ExactStepIdentity,
} from './lib/exact-step-identity';
import {
  ExactTreatmentPlanBuilder,
  convertExactAppointmentStatus,
} from './lib/exact-treatment-plan-builder';
import { PatientDestinationEntity } from './patient';
import { PatientTreatmentPlanDestinationEntity } from './patient-treatment-plans';
import { StafferDestinationEntity } from './staff';
import { AppointmentCategoryDestination } from '../../../mappings/appointment-to-treatment-category-to-xlsx';

export const PATIENT_APPOINTMENT_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: PATIENT_APPOINTMENT_RESOURCE_TYPE,
    label: 'Patient Appointments',
    description: '',
  },
});

export interface IExactAppointmentJobData
  extends IPatientAppointmentJobData<ExactPatient> {
  appointmentRoomToPractitioner: WithPrimaryKey<ITranslationMap<IStaffer>>[];
  appointmentToTreatmentCategory: WithPrimaryKey<
    ITranslationMap<AppointmentCategoryDestination>
  >[];
  sourceItemCodes: WithPrimaryKey<
    ITranslationMap<object, ItemCodeResourceMapType>
  >[];
  staffToPractice: WithPrimaryKey<ITranslationMap<IPractice>>[];
}

export class PatientAppointmentDestinationEntity extends BasePatientAppointmentDestinationEntity<
  ExactPatient,
  IExactAppointmentJobData
> {
  destinationEntity = PATIENT_APPOINTMENT_DESTINATION_ENTITY;
  patientSourceEntity = new PatientSourceEntity();

  override canMigrateByDateRange = true;
  override batchLimit = 10000;

  override filters = [
    new PatientIdFilter<IExactAppointmentJobData>((jobData) =>
      jobData.sourcePatient.data.patient_id.toString()
    ),
  ];

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    appointments: new AppointmentSourceEntity(),
    notes: new PatientNotesSourceEntity(),
    treatments: new PatientTreatmentSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
    staff: new StafferDestinationEntity(),
    treatmentPlans: new PatientTreatmentPlanDestinationEntity(),
  };

  customMappings = {
    staff: new ExactStafferMappingHandler(),
    appointmentRoomToPractitioner:
      new AppointmentRoomToPractionerMappingHandler(),
    appointmentToTreatmentCategory:
      new AppointmentCategoryToTreatmentCategoryMappingHandler(),
    itemCodes: new ExactItemCodeMappingHandler(),
    staffToPractice: new StaffToPracticeMapping(),
  };

  /**
   * Exact appointments are linked to the step the treatment plan job saved for
   * them. Any drift between that link and the step it points to fails the
   * patient rather than guessing another step.
   */
  static async resolveAssociatedPlan(
    translationMap: TranslationMapHandler,
    patientRef: DocumentReference<IPatient>,
    sourcePatientId: string,
    appointmentUid: string
  ): Promise<IAssociatedTreatment | undefined> {
    const link = await translationMap.getBySource<ITreatmentStep>(
      ExactStepIdentity.appointmentLinkKey(sourcePatientId, appointmentUid),
      EXACT_APPOINTMENT_STEP_LINK_RESOURCE_TYPE
    );
    if (!link) {
      return;
    }
    if (!link.destinationIdentifier) {
      throw new Error(`Appointment link for ${appointmentUid} is incomplete`);
    }

    const stepPath = link.destinationIdentifier.path;
    const linkedStep = `Linked treatment step ${stepPath} for appointment ${appointmentUid}`;
    const step = await Firestore.safeGetDoc(link.destinationIdentifier);
    if (!step) {
      throw new Error(`${linkedStep} is missing`);
    }
    if (step.deleted) {
      throw new Error(`${linkedStep} is deleted`);
    }
    if (!stepPath.startsWith(`${patientRef.path}/`)) {
      throw new Error(`${linkedStep} belongs to another patient`);
    }
    const isAnchored =
      'sourceIdentifier' in step &&
      isString(step.sourceIdentifier) &&
      ExactStepIdentity.isAnchoredToAppointment(
        step.sourceIdentifier,
        appointmentUid
      );
    if (!isAnchored) {
      throw new Error(`${linkedStep} is not anchored to the appointment`);
    }

    const plan = await TreatmentStep.treatmentPlan(step);
    if (plan.deleted) {
      throw new Error(`${linkedStep} has a deleted treatment plan`);
    }
    return TreatmentPlan.treatmentStepToAssociatedTreatment(plan, step);
  }

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IExactAppointmentJobData[]> {
    const brand$ = PracticeMigration.brand$(migration).pipe(
      map(toNamedDocument)
    );
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const practitioners$ = brand$.pipe(
      switchMap((brand) => Firestore.getDocs(Brand.stafferCol(brand))),
      toINamedDocuments()
    );
    const appointmentRoomToPractitioner$ =
      this.customMappings.appointmentRoomToPractitioner.getRecords(
        translationMap
      );
    const appointmentToTreatmentCategory$ =
      this.customMappings.appointmentToTreatmentCategory.getRecords(
        translationMap
      );
    const sourceItemCodes$ =
      this.customMappings.itemCodes.getRecords(translationMap);
    const staffToPractice$ =
      this.customMappings.staffToPractice.getRecords(translationMap);

    return snapshotCombineLatest([
      brand$,
      staff$,
      practitioners$,
      appointmentRoomToPractitioner$,
      appointmentToTreatmentCategory$,
      sourceItemCodes$,
      staffToPractice$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([
          patients,
          [
            brand,
            staff,
            practitioners,
            appointmentRoomToPractitioner,
            appointmentToTreatmentCategory,
            sourceItemCodes,
            staffToPractice,
          ],
        ]) =>
          patients.map((sourcePatient) => ({
            sourcePatient,
            brand,
            staff,
            practitioners,
            appointmentRoomToPractitioner,
            appointmentToTreatmentCategory,
            sourceItemCodes,
            staffToPractice,
          }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IExactAppointmentJobData
  ): Promise<
    | IPatientAppointmentMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
    | (IDestinationEntityRecord & SkippedDestinationEntityRecord)
  > {
    const sourcePatientId = this.sourceEntities.patients.getSourceRecordId(
      data.sourcePatient.data
    );

    const patientRef = await translationMap.getDestination<IPatient>(
      sourcePatientId.toString(),
      PATIENT_RESOURCE_TYPE
    );

    if (!patientRef) {
      return this._buildErrorResponse(
        data.sourcePatient,
        `No patient with id ${sourcePatientId}`
      );
    }

    const patient = await Firestore.getDoc(patientRef);

    const sourceAppointments =
      await this.sourceEntities.appointments.filterRecords(
        migration,
        'patientId',
        data.sourcePatient.data.patient_id.toString()
      );

    try {
      const planUid =
        ExactTreatmentPlanBuilder.getPlanIdentifier(sourcePatientId);
      const defaultPlanName = getDefaultTreatmentPlanName(migration);
      const steps: IStepWithIdentifiers[] = [];

      const appointments: (IPatientAppointmentBuildData | undefined)[] =
        await resolveSequentially(
          sourceAppointments,
          async (sourceAppointment) => {
            const appointmentUid =
              this.sourceEntities.appointments.getSourceRecordId(
                sourceAppointment.data
              );

            const practitioner = await this.resolvePractitioner(
              data,
              translationMap,
              sourceAppointment.data
            );
            if (!practitioner) {
              throw new Error(
                `No practitioner found for id ${sourceAppointment.data.practitioner_initials}`
              );
            }

            const practice = await resolveExactStaffLocation(
              sourceAppointment.data.practitioner_initials,
              translationMap,
              data.staffToPractice,
              migration.configuration.practices
            );
            if (!practice) {
              throw new Error(
                `No practice found for practitioner ${sourceAppointment.data.practitioner_initials}`
              );
            }

            const [appointment, interactions, schedulingEvent] =
              await this.buildAppointmentData(
                migration,
                patient,
                sourceAppointment,
                practitioner,
                practice,
                translationMap
              );

            if (!appointment) {
              return;
            }

            const stepUid = ExactStepIdentity.placeholderStepUid(
              sourcePatientId,
              appointmentUid
            );

            const timezone = migration.configuration.timezone;
            const appointmentDate = toMomentTz(
              sourceAppointment.record.translations.appointmentDateTime,
              timezone
            ).format(HISTORY_DATE_FORMAT);

            const categoryDestinationIdentifier =
              data.appointmentToTreatmentCategory.find(
                (categoryMap) =>
                  categoryMap.sourceLabel ===
                  sourceAppointment.data.appointment_category
              )?.associatedValue;

            const categoryDestination = isINamedDocument(
              categoryDestinationIdentifier
            )
              ? await Firestore.getDoc(categoryDestinationIdentifier.ref)
              : undefined;

            const overrideTreatmentCategory = isTag(categoryDestination)
              ? undefined
              : (categoryDestination?.ref as DocumentReference<ITreatmentCategory>);
            if (appointment.treatmentPlan && overrideTreatmentCategory) {
              appointment.treatmentPlan.treatmentStep.display = {
                ...appointment.treatmentPlan.treatmentStep.display,
                overrideTreatmentCategory,
              };
            }

            const appointmentTag = isTag(categoryDestination)
              ? (categoryDestination as INamedDocument<ITag>)
              : undefined;
            if (appointmentTag) {
              appointment.tags.push(appointmentTag);
            }

            if (!appointment.treatmentPlan) {
              steps.push({
                ...TreatmentStep.init({
                  name: sourceAppointment.data.treatment_name.length
                    ? sourceAppointment.data.treatment_name
                    : `Exact Appointment - ${appointmentDate}`,
                  status:
                    appointment.status === AppointmentStatus.Complete
                      ? TreatmentStepStatus.Complete
                      : TreatmentStepStatus.Incomplete,
                  schedulingRules: {
                    duration: appointment.event
                      ? Event.duration(appointment.event)
                      : 0,
                  },
                  display: {
                    overrideTreatmentCategory,
                  },
                }),
                sourceIdentifier: stepUid,
                createdAt: appointment.createdAt,
                date: appointmentDate,
              });
            }

            return {
              sourcePatientId: sourcePatientId.toString(),
              patientRef,
              appointmentUid,
              createdAt:
                sourceAppointment.record.translations.appointmentDateTime,
              appointment,
              planUid,
              stepUid,
              interactions,
              schedulingEvent,
            };
          }
        );

      return {
        patientRef,
        sourcePatientId: sourcePatientId.toString(),
        appointments: compact(appointments),
        planPairs: [
          {
            plan: {
              ...TreatmentPlan.init({
                name: defaultPlanName,
                status: TreatmentPlanStatus.InProgress,
              }),
              sourceIdentifier: planUid,
            },
            steps: uniqBy(steps, (step) => step.sourceIdentifier),
          },
        ],
      };
    } catch (error) {
      return this._buildErrorResponse(data.sourcePatient, getError(error));
    }
  }

  async resolvePractitioner(
    data: IExactAppointmentJobData,
    translationMap: TranslationMapHandler,
    sourceAppointment: IExactAppointment
  ): Promise<WithRef<IStaffer> | undefined> {
    if (sourceAppointment.room_id) {
      const appointmentBookPractitionerMap =
        data.appointmentRoomToPractitioner.find(
          (practitionerMap) =>
            practitionerMap.sourceIdentifier === sourceAppointment.room_id
        );

      if (appointmentBookPractitionerMap?.sourceLink?.sourceIdentifier) {
        return resolveExactStaffer(
          appointmentBookPractitionerMap.sourceLink.sourceIdentifier,
          translationMap,
          data.staff
        );
      }
    }

    return resolveExactStaffer(
      sourceAppointment.practitioner_initials,
      translationMap,
      data.staff
    );
  }

  async buildAppointmentData(
    migration: WithRef<IPracticeMigration>,
    patient: WithRef<IPatient>,
    sourceAppointment: IGetRecordResponse<ExactAppointment>,
    practitioner: WithRef<IStaffer>,
    practice: WithRef<IPractice>,
    translationMap: TranslationMapHandler
  ): Promise<
    | [
        IPatientAppointmentBuildData['appointment'],
        IInteractionV2[],
        ISchedulingEvent?,
      ]
    | [undefined, undefined]
  > {
    // Their table does not contain a created_at date so we will use the appointment date
    const createdAt = sourceAppointment.record.translations.appointmentDateTime;
    const status = convertExactAppointmentStatus(
      sourceAppointment.data.appointment_status
    );
    const interactions: IInteractionV2[] = await this.buildInteractions(
      sourceAppointment,
      migration
    );

    if (sourceAppointment.data.appointment_note) {
      interactions.unshift(
        Interaction.init({
          type: InteractionType.Note,
          title: [toTextContent('Appointment note')],
          content: initVersionedSchema(sourceAppointment.data.appointment_note),
          pinned: true,
          createdAt,
        })
      );
    }

    if (status === AppointmentStatus.Unscheduled) {
      return [undefined, undefined];
    }

    const appointmentEvent = this.getAppointmentEvent(
      sourceAppointment,
      patient,
      practice,
      practitioner
    );

    const associatedPlan =
      await PatientAppointmentDestinationEntity.resolveAssociatedPlan(
        translationMap,
        patient.ref,
        sourceAppointment.data.patient_id.toString(),
        sourceAppointment.data.appointment_id
      );

    const appointment: IPatientAppointmentBuildData['appointment'] = {
      event: appointmentEvent,
      eventHistory: [
        {
          event: appointmentEvent,
          createdAt,
        },
      ],
      status,
      statusHistory: [],
      practice: toNamedDocument(practice),
      practitioner: stafferToNamedDoc(practitioner),
      cancellationHistory: [],
      dependencies: [],
      tags: [],
      treatmentPlan: associatedPlan,
      ...initFirestoreModel(),
    };

    let schedulingEvent: ISchedulingEvent | undefined;
    if (
      sourceAppointment.data.appointment_status ===
      ExactAppointmentStatus.Failed
    ) {
      const key = SchedulingEventReason.getMetadataKey(
        CancellationReason.FailedToAttend
      );
      const schedulingEventReason = await Firestore.firstResult(
        Brand.cancellationReasonCol(migration.configuration.brand),
        where('metadataKey', '==', key)
      );
      if (!schedulingEventReason) {
        throw new Error(
          `Failed to find scheduling event reason for FTA with key ${key}`
        );
      }

      schedulingEvent = SchedulingEvent.init({
        scheduledByPractice: appointment.practice.ref,
        reason: schedulingEventReason.ref,
        reasonSetManually: false,
        schedulingConditions: {
          eventType: SchedulingEventType.Cancel,
          moveToSameDayDefault: false,
          fillGapDefault: false,
        },
      });
    }

    return [appointment, interactions, schedulingEvent];
  }

  async buildInteractions(
    sourceAppointment: IGetRecordResponse<ExactAppointment>,
    migration: WithRef<IPracticeMigration>
  ): Promise<IInteractionV2[]> {
    const notes = await this.sourceEntities.notes.filterRecords(
      migration,
      'appointmentId',
      sourceAppointment.data.appointment_id
    );
    return notes.map((note) => ({
      ...Interaction.init({
        type: InteractionType.Note,
        title: [toTextContent('Appointment note')],
        content: initVersionedSchema(note.data.note),
        createdAt: note.record.translations.entryDate,
      }),
    }));
  }

  getAppointmentEvent(
    sourceAppointment: IGetRecordResponse<ExactAppointment>,
    patient: WithRef<IPatient>,
    practice: WithRef<IPractice>,
    practitioner: WithRef<IStaffer>
  ): IEvent {
    const from = toMomentTz(
      sourceAppointment.record.translations.appointmentDateTime,
      practice.settings.timezone
    );
    const to = toTimestamp(
      from
        .clone()
        .add(moment.duration(sourceAppointment.data.appointment_duration))
    );
    return Event.init({
      from: toTimestamp(from),
      to,
      practice: toNamedDocument(practice),
      type: EventType.Appointment,
      participants: [
        {
          ...toNamedDocument(patient),
          type: ParticipantType.Patient,
        },
        {
          ...stafferToNamedDoc(practitioner),
          type: ParticipantType.Staffer,
        },
      ],
    });
  }
}
